import { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import Sort from "../../assets/sort.svg";
import { FilterDialogFooter } from "../../views/users/components/filter_dialog/filter_dialog_parts";
import { black, purple, purpleLight } from "../../theme/colors";

export interface SortOption<T extends string> {
  value: T;
  label: string;
}

// Generic "Sort by" dialog, styled to match the Filters dialogs across the
// app (FilterDialogFooter, same header/radius conventions). Each screen
// supplies its own option list and value type (the backend sortBy/sortOrder
// field names differ per module), this component only owns the popup shell
// and single-select behavior.
export default function SortPopup<T extends string>({
  open,
  onClose,
  title = "Sort",
  options,
  value,
  onApply,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  options: SortOption<T>[];
  value: T;
  onApply: (value: T) => void;
}) {
  const [_value, _setValue] = useState<T>(value);

  useEffect(() => {
    if (open) _setValue(value);
  }, [open, value]);

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ minWidth: 340, maxWidth: "90vw", p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: purpleLight,
                flexShrink: 0,
              }}
            >
              <img src={Sort} width={18} height={18} alt="" />
            </Box>
            <Typography fontWeight={700} fontSize={17} color={black}>
              {title}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ "&:hover": { bgcolor: purpleLight } }}>
            <CloseRounded fontSize="small" sx={{ color: "#5B4A5A" }} />
          </IconButton>
        </Box>

        <RadioGroup value={_value} onChange={(e) => _setValue(e.target.value as T)}>
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio sx={{ color: purple, "&.Mui-checked": { color: purple } }} />}
              label={option.label}
              sx={{ mx: 0, borderRadius: 2, "&:hover": { bgcolor: purpleLight } }}
            />
          ))}
        </RadioGroup>

        <Box mt={2}>
          <FilterDialogFooter
            applyLabel="Apply"
            onCancel={onClose}
            onApply={() => {
              onApply(_value);
              onClose();
            }}
          />
        </Box>
      </Box>
    </Dialog>
  );
}
